'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { useTheme, ThemeToggle } from '@/components/ThemeProvider'
import WorkflowAnimation from '@/components/WorkflowAnimation'
import { TerminalAnimation } from '@/components/Effects'
import PhoneWidget from '@/components/PhoneWidget'
import HeroShowcase from '@/components/HeroShowcase'
import OrgChart from '@/components/OrgChart'
import Navbar from '@/components/Navbar'

/* Google Font */
const garamondLink = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap'

const serifStyle: React.CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }
const serifLight: React.CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }

const defaultFeatures = [
  { num: '01', title: 'Arabic & English', desc: 'Every AI employee speaks Arabic and English fluently. Saudi-first, global-ready.' },
  { num: '02', title: 'WhatsApp & Telegram', desc: 'Deploy on the platforms your customers already use. No new app required.' },
  { num: '03', title: 'Train on Your Data', desc: 'Upload your docs, FAQs and pricing. Your AI learns your business in minutes.' },
  { num: '04', title: 'Analytics Dashboard', desc: 'See every conversation, track performance, and improve continuously.' },
  { num: '05', title: 'Isolated Environments', desc: 'Each AI employee runs in a separate environment. Your data never mixes.' },
  { num: '06', title: 'Live in Minutes', desc: 'No code, no engineers. Hire, configure, and deploy your first employee today.' },
]

const defaultSteps = [
  { num: '01', title: 'Browse the Marketplace', desc: 'Pick from 30+ pre-built AI employees across Sales, Support, Finance, HR, and custom roles.' },
  { num: '02', title: 'Hire and Configure', desc: 'Give your AI employee a name, upload your documents, and set their tone. Under 10 minutes.' },
  { num: '03', title: 'Deploy to WhatsApp or Telegram', desc: 'Your AI employee goes live immediately. Customers can message them right now.' },
]

const defaultEmployees = [
  { role: 'Sales', name: 'Aria', desc: 'Qualifies leads, sends proposals, follows up and closes — on WhatsApp, around the clock.', price: '$400/mo', tagBg: '#FFF7ED', tagColor: '#9a3412' },
  { role: 'Support', name: 'Kira', desc: 'Handles tickets, returns, FAQs and escalations. Your customers never wait again.', price: '$400/mo', tagBg: '#F0FDF4', tagColor: '#166534' },
  { role: 'Finance', name: 'Zara', desc: 'Sends invoices, tracks payments, generates reports and answers billing questions instantly.', price: '$400/mo', tagBg: '#EFF6FF', tagColor: '#1e40af' },
  { role: 'HR', name: 'Mira', desc: 'Screens CVs, schedules interviews, answers policy questions and manages onboarding.', price: '$400/mo', tagBg: '#FAF5FF', tagColor: '#6b21a8' },
]

const consultationAgents = [
  { id: 'victor', name: 'Auris', role: 'Investment Strategist', dept: 'Financial Advisory' },
  { id: 'marcus', name: 'Strix', role: 'Strategy Consultant', dept: 'Managerial Consultancy' },
  { id: 'nadia', name: 'Lex', role: 'Senior Consultant - Zakat & Tax', dept: 'Tax & Legal' },
  { id: 'amin', name: 'Vera', role: 'Senior Consultant - Internal Audit', dept: 'Audit & Assurance' },
  { id: 'bader', name: 'Axis', role: 'Senior Consultant - Business Valuation', dept: 'Deals & Transactions' },
  { id: 'zeina', name: 'Nova', role: 'Senior Consultant - Digital Transformation', dept: 'Digital & Technology' },
  { id: 'lara', name: 'Terra', role: 'Senior Consultant - ESG & Sustainability', dept: 'ESG & Government' },
];

const defaultUseCases = [
  {
    id: 'sales', roleLabel: 'Sales Agent — Aria', roleColor: '#9a3412',
    title: 'Closes deals', titleItalic: 'while you sleep',
    desc: 'Aria qualifies every inbound lead on WhatsApp, sends personalised proposals, follows up automatically, and only escalates when a deal is ready to close.',
    bullets: ['Qualifies leads with smart, contextual questions', 'Sends pricing and proposals automatically', 'Follows up until the customer responds', 'Arabic and English, out of the box'],
    hireName: 'Aria',
    preview: [
      { from: 'user', text: 'How much for 10 licenses?' },
      { from: 'agent', text: "Hi! For 10 users I'd recommend our Starter plan at $149/mo — WhatsApp + Telegram, unlimited messages, full Arabic support. Want me to send a proposal?" },
      { from: 'user', text: 'Yes, to my email please' },
      { from: 'agent', text: 'Done. Proposal sent ✓ I\'ve also set a follow-up for Friday. Anything else?' },
    ],
  },
  {
    id: 'support', roleLabel: 'Support Agent — Kira', roleColor: '#166534',
    title: 'Zero wait time,', titleItalic: 'every single ticket',
    desc: 'Kira resolves 80% of tickets instantly from your knowledge base, and escalates the rest with full context — so your team only handles what matters.',
    bullets: ['Trained on your docs, FAQs and policies', 'Handles returns, orders, and complaints', 'Escalates with full conversation summary', 'Available on WhatsApp 24/7'],
    hireName: 'Kira',
    preview: [
      { from: 'user', text: "My order hasn't arrived after 5 days" },
      { from: 'agent', text: "I'm sorry to hear that. Can you share your order number so I can check right away?" },
      { from: 'user', text: 'ORD-48291' },
      { from: 'agent', text: 'Found it. Your order is in transit — estimated delivery tomorrow by 6 PM. Flagged as priority. You\'ll receive a tracking SMS shortly.' },
    ],
  },
  {
    id: 'finance', roleLabel: 'Finance Agent — Zara', roleColor: '#1e40af',
    title: 'Finance on autopilot,', titleItalic: 'zero delays',
    desc: 'Zara sends invoices, chases payments, generates financial summaries and answers billing questions — so your finance team focuses on strategy.',
    bullets: ['Automated invoice sending and reminders', 'Monthly financial summaries on demand', 'Payment status updates via WhatsApp'],
    hireName: 'Zara',
    preview: [
      { from: 'user', text: "Send me February's revenue summary" },
      { from: 'agent', text: "February Summary\n\nRevenue: SAR 284,500\nOutstanding: SAR 32,000 (3 invoices)\nvs January: +12.4%\n\nWant the full PDF report?" },
    ],
  },
  {
    id: 'hr', roleLabel: 'HR Agent — Mira', roleColor: '#6b21a8',
    title: 'Hiring and HR,', titleItalic: 'handled automatically',
    desc: 'Mira screens candidates, schedules interviews, answers policy questions, and manages onboarding — all via WhatsApp.',
    bullets: ['CV screening and shortlisting', 'Interview scheduling and confirmations', 'Employee policy Q&A, 24 hours a day'],
    hireName: 'Mira',
    preview: [
      { from: 'user', text: 'How many days annual leave do I have left?' },
      { from: 'agent', text: 'Hi Ahmed. You have 18 days remaining of your 25-day annual leave. Your last approved request was 14–17 January. Would you like to submit a new request?' },
    ],
  },
]

const defaultPricing = [
  {
    name: 'Starter', price: '$49', period: '/mo',
    desc: 'per AI employee',
    features: ['1 AI Employee', '1,000 messages/mo', 'WhatsApp or Telegram', 'Basic dashboard'],
    cta: 'Get started', popular: false,
  },
  {
    name: 'Business', price: '$149', period: '/mo',
    desc: 'up to 5 AI employees',
    features: ['5 AI Employees', '10,000 messages/mo', 'WhatsApp + Telegram', 'Advanced analytics', 'Custom branding'],
    cta: 'Get started', popular: true,
  },
  {
    name: 'Enterprise', price: 'Custom', period: '',
    desc: 'full AI department',
    features: ['Unlimited employees', 'Unlimited messages', 'Custom AI training', 'Dedicated support + SLA'],
    cta: 'Contact sales', popular: false,
  },
]


const departmentPricing = [
  {
    name: 'Pro', price: '$400', period: '/mo',
    desc: 'per AI employee',
    features: ['1 AI Employee of your choice', 'WhatsApp or Telegram', '10,000 messages/mo', 'Train on your docs & FAQs', 'Analytics dashboard', '24/7 uptime'],
    cta: 'Hire Your First Employee', popular: true,
    note: '1 month Anthropic max subscription included',
  },
]
const agentPricing = [
  {
    name: 'Pro', price: '$240', period: '/mo',
    desc: 'Dedicated server for growing businesses',
    features: ['Super Agent powered by Claude', '1 vCPU · 4 GB RAM dedicated server', 'WhatsApp & Telegram channels', 'All skills & integrations', '24/7 uptime & support'],
    cta: 'Deploy Your Agent', popular: false,
    note: '1 month Anthropic max subscription included',
  },
  {
    name: 'Ultra', price: '$350', period: '/mo',
    desc: 'High-performance for busy teams',
    features: ['Super Agent powered by Claude', '4 vCPU · 16 GB RAM dedicated server', '4x more CPU power & RAM', 'WhatsApp & Telegram channels', 'All skills & integrations', '24/7 uptime & support'],
    cta: 'Deploy Your Agent', popular: true,
    note: '1 month Anthropic max subscription included',
  },
]

const defaultFaq = [
  { q: 'Do I need technical skills to hire an AI employee?', a: 'Not at all. Pick an employee from the marketplace, give them your documents and brand voice, and deploy. No code, no API keys, no engineers needed.' },
  { q: 'How does WhatsApp connection work?', a: 'We use a pairing code system. Enter your phone number, get a code, type it into WhatsApp\'s "Link a Device" feature. Takes 30 seconds.' },
  { q: 'Can I hire more than one AI employee?', a: 'Yes. Each employee is billed separately — start with one, then add more as you grow. The Business plan includes up to 5.' },
  { q: 'What languages do AI employees speak?', a: 'Arabic and English are built-in for all employees. Additional languages available on request.' },
  { q: 'Is my data secure?', a: 'Each AI employee runs in an isolated environment. All data is encrypted. We never see, store, or have access to your conversations.' },
  { q: 'Can I cancel anytime?', a: 'Yes. No contracts, no commitments. Cancel anytime with one click.' },
]

const defaultTestimonials = [
  { text: "Hired Aria for sales and she handles 80% of leads now. I can finally focus on the actual work.", name: 'Sarah K.', role: 'E-commerce Owner' },
  { text: "Kira resolved 73% of our support tickets in the first week. The ROI was immediate.", name: 'Ahmed R.', role: 'Tech Startup CTO' },
  { text: "Zara sends all our invoices and payment reminders automatically. Our collection rate went up 40%.", name: 'Dr. Noor M.', role: 'Clinic Owner' },
]

const defaultCta = {
  title: 'Build your AI department',
  titleHighlight: 'today',
  subtitle: 'Start with one AI employee. Scale to a full department. No engineers required.',
  buttonText: 'Browse AI Employees',
}

export default function HomePage() {
  const [content, setContent] = useState<any>(null)
  const [mounted, setMounted] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [pricingTab, setPricingTab] = useState<'department'|'agent'>('department')
  const { theme } = useTheme()

  useEffect(() => { setMounted(true) }, [])
  useEffect(() => {
    fetch('/api/site-content').then(r => r.json()).then(setContent).catch(() => {})
    fetch('/api/profile').then(r => r.ok ? r.json() : null).then(u => { if (u) setUser(u) }).catch(() => {})
  }, [])

  const logoConfig = content?._logo || null
  const navLogo = theme === 'dark' ? (logoConfig?.whiteUrl || '/logo-white.png') : (logoConfig?.darkUrl || '/logo-dark.png')
  const headerPx = logoConfig?.headerSize || 28
  const footerPx = logoConfig?.footerSize || 40
  const logoReady = content !== null

  const footerConfig = content?._footer || {}
  const socialLinks = footerConfig.social || []

  const features = content?.features?.length ? content.features : defaultFeatures
  const steps = content?.steps?.length ? content.steps : defaultSteps
  const employees = defaultEmployees
  const useCases = defaultUseCases
  const testimonials = content?.testimonials?.length ? content.testimonials : defaultTestimonials
  const pricing = content?.pricing?.length ? content.pricing : defaultPricing
  const faq = content?.faq?.length ? content.faq : defaultFaq
  const cta = { ...defaultCta, ...(content?.cta || {}) }

  const tabIds = ['sales', 'support', 'finance', 'hr']

  return (
    <div className={`min-h-screen bg-[#FAFAF8] dark:bg-gray-950 overflow-x-hidden transition-opacity duration-200 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
      <link rel="stylesheet" href={garamondLink} />

      <Navbar activePage="home" />

      {/* ── Hero ── */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <div>
            {/* Eyebrow claim */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              <div className="flex items-center gap-1.5 bg-[#1a1a1a] dark:bg-white rounded-full px-3.5 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
                <span className="text-[11px] font-semibold text-white dark:text-[#1a1a1a] tracking-wide">World&apos;s First</span>
              </div>
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Hybrid Human + AI SaaS Platform</span>
            </div>
            <h1 style={serifStyle} className="text-5xl sm:text-6xl lg:text-7xl text-[#1a1a1a] dark:text-white leading-[1.06] mb-5">
              Your <span style={{fontStyle:"normal",fontWeight:800}}>Hybrid</span><br/>AI Department,<br/><em className="italic text-gray-400">led by you</em>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-lg">
              The first hybrid platform where <strong className="text-[#1a1a1a] dark:text-white font-semibold">you as human manager</strong> can lead a full AI team — Consultant, Marketing, Legal, and HR — on Email, WhatsApp or Telegram. 24/7, no salary, no sick days.
            </p>
            <div className="flex flex-wrap gap-3 items-center mb-10">
              <Link href="/ai-employee-marketplace" className="bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] px-7 py-3 rounded-full text-sm font-semibold hover:bg-black dark:hover:bg-gray-200 transition">
                Browse AI Employees
              </Link>
              <a href="#how-it-works" className="text-sm text-gray-500 dark:text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition flex items-center gap-1.5">
                See how it works <span className="text-lg leading-none">→</span>
              </a>
            </div>
            <div className="flex gap-8 pt-6 border-t border-[#E8E8E4] dark:border-gray-800">
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">30+</div><div className="text-xs text-gray-400 mt-1">AI Employees</div></div>
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">4</div><div className="text-xs text-gray-400 mt-1">Departments</div></div>
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">30m</div><div className="text-xs text-gray-400 mt-1">Time to Deploy</div></div>
            </div>
          </div>

          {/* Right — Org Chart */}
          <div className="flex justify-center items-center relative">
            <div className="absolute inset-0 bg-gradient-radial from-emerald-500/8 to-transparent rounded-full pointer-events-none"></div>
            <OrgChart />
          </div>
        </div>
      </section>

      {/* ── Proof Bar ── */}
      <section className="py-5 px-4 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-8 sm:gap-14 flex-wrap">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-300 dark:text-gray-600">Powered by</span>
          {['Claude', 'WhatsApp Business API', 'Telegram', 'OpenAI', 'ElevenLabs Voice'].map(b => (
            <span key={b} className="text-xs font-semibold text-gray-400 dark:text-gray-500">{b}</span>
          ))}
        </div>
      </section>

      {/* ── Meet Your Team ── */}
      <section id="department" className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-5">
            <div>
              <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Marketplace</div>
              <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Meet your new team</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-3 text-base max-w-md leading-relaxed">Pre-built AI employees, each trained for their role. Hire one or build a full department.</p>
            </div>
            <Link href="/ai-employee-marketplace" className="text-sm font-medium text-[#1a1a1a] dark:text-white border border-[#E8E8E4] dark:border-gray-700 px-5 py-2.5 rounded-full hover:border-[#1a1a1a] dark:hover:border-white transition flex-shrink-0">
              View all 30+ employees →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {employees.map((emp) => (
              <Link key={emp.name} href="/ai-employee-marketplace" className="block bg-white dark:bg-gray-900 border border-[#E8E8E4] dark:border-gray-800 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer">
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4" style={{ background: emp.tagBg, color: emp.tagColor }}>{emp.role}</span>
                <div style={serifStyle} className="text-2xl text-[#1a1a1a] dark:text-white mb-1">{emp.name}</div>
                <div className="text-xs text-gray-400 mb-3">{emp.role} Agent</div>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{emp.desc}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#1a1a1a] dark:text-white">From {emp.price}</span>
                  <span className="bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] text-xs font-medium px-4 py-2 rounded-full hover:bg-gray-800 dark:hover:bg-gray-200 transition">Hire</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Consultation & Advisory Section ── */}
      <section style={{background:'#0a0a0a'}} className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
                <span className="text-xs font-semibold tracking-widest uppercase text-amber-400">Consultation &amp; Advisory</span>
              </div>
              <h2 style={serifStyle} className="text-4xl sm:text-5xl text-white">Big 4-level expertise.<br/><em className="italic text-amber-400/80">AI speed.</em></h2>
              <p className="text-gray-400 mt-3 text-base max-w-lg leading-relaxed">Senior AI advisors covering investment strategy, wealth management, organizational design, and executive coaching — at a fraction of consulting firm rates.</p>
            </div>
            <Link href="/ai-employee-marketplace" className="text-sm font-medium text-white border border-white/20 px-5 py-2.5 rounded-full hover:border-white/60 transition flex-shrink-0 whitespace-nowrap">
              View all advisors →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            {consultationAgents.map((agent) => (
              <Link key={agent.id} href="/ai-employee-marketplace" className="block rounded-2xl p-5 border border-white/10 hover:border-amber-500/40 hover:-translate-y-1 transition-all duration-200 cursor-pointer group" style={{background:'#111111'}}>
                <div className="text-[10px] font-semibold tracking-wide uppercase text-amber-500/80 mb-3">{agent.dept}</div>
                <div style={serifStyle} className="text-xl text-white mb-1">{agent.name}</div>
                <div className="text-xs text-gray-500 mb-4">{agent.role}</div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-300">$400/mo</span>
                  <span className="bg-amber-500 group-hover:bg-amber-400 text-black text-xs font-semibold px-3 py-1.5 rounded-full transition">Hire</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Structure Section ── */}
      {content?.structureSection?.enabled !== false && (
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-4">How It Works</div>
          <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white mb-4">Your team,<br/><em className="italic text-gray-400">led by you</em></h2>
          <p className="text-base text-gray-500 dark:text-gray-400 max-w-lg leading-relaxed mb-14">You set the strategy. Your AI employees handle the execution — sales, support, finance, HR — reporting to you around the clock.</p>

          {/* ── Dark Infographic Card ── */}
          <div className="relative rounded-[28px] overflow-hidden" style={{background:'#0f0f0f', boxShadow:'0 32px 80px rgba(0,0,0,0.18)'}}>
            {/* Grid background */}
            <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)',backgroundSize:'48px 48px'}}></div>
            {/* Top glow */}
            <div className="absolute pointer-events-none" style={{top:'-80px',left:'50%',transform:'translateX(-50%)',width:'600px',height:'300px',background:'radial-gradient(ellipse,rgba(74,222,128,0.08) 0%,transparent 70%)'}}></div>
            {/* Bottom glow */}
            <div className="absolute pointer-events-none" style={{bottom:'-60px',left:'50%',transform:'translateX(-50%)',width:'900px',height:'300px',background:'radial-gradient(ellipse,rgba(99,102,241,0.07) 0%,transparent 70%)'}}></div>

            <div className="relative z-10 p-10 sm:p-14">
              {/* Label */}
              <div className="flex justify-center mb-10">
                <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#777] border border-[#2a2a2a] px-4 py-1.5 rounded-full">AI Department Structure</span>
              </div>

              {/* Manager Card */}
              <div className="flex justify-center mb-0">
                <div className="relative flex items-center gap-5 rounded-[20px] px-8 py-5 min-w-[340px]" style={{background:'linear-gradient(135deg,#1a1a1a,#222)',border:'1px solid #2f2f2f',boxShadow:'0 0 0 1px rgba(255,255,255,0.04),0 8px 32px rgba(0,0,0,0.4)'}}>
                  {/* Green top accent */}
                  <div className="absolute top-0 left-7 right-7 h-px" style={{background:'linear-gradient(90deg,transparent,#4ade80,transparent)'}}></div>
                  {/* Photo or icon */}
                  {content?.structureSection?.managerPhoto ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={content.structureSection.managerPhoto} alt={content.structureSection.managerName || 'Manager'} className="w-13 h-13 rounded-full object-cover flex-shrink-0" style={{width:52,height:52,border:'1px solid rgba(74,222,128,0.25)'}} />
                  ) : (
                    <div className="w-[52px] h-[52px] rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#1c4a2e,#166534)',border:'1px solid rgba(74,222,128,0.25)'}}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[15px] text-white mb-0.5">{content?.structureSection?.managerName || 'You — Department Head'}</div>
                    <div className="text-[12px] text-[#999]">{content?.structureSection?.managerTitle || 'Sets goals · Approves escalations · Reviews reports'}</div>
                  </div>
                  <div className="ml-auto flex flex-col items-end gap-1.5">
                    <div className="text-[10px] font-semibold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full" style={{background:'rgba(74,222,128,0.1)',border:'1px solid rgba(74,222,128,0.25)',color:'#4ade80'}}>Human Manager</div>
                    <div className="flex items-center gap-1.5 text-[11px] text-[#999]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80]" style={{boxShadow:'0 0 8px #4ade80'}}></span>Active
                    </div>
                  </div>
                </div>
              </div>

              {/* SVG Connector */}
              <svg width="100%" height="80" viewBox="0 0 900 80" preserveAspectRatio="xMidYMid meet" className="block" style={{overflow:'visible'}}>
                <defs>
                  <linearGradient id="lgV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity="0.6"/>
                    <stop offset="100%" stopColor="#4ade80" stopOpacity="0.15"/>
                  </linearGradient>
                  <linearGradient id="lgH" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0"/>
                    <stop offset="20%" stopColor="#2a2a2a" stopOpacity="1"/>
                    <stop offset="80%" stopColor="#2a2a2a" stopOpacity="1"/>
                    <stop offset="100%" stopColor="#1a1a1a" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <line x1="450" y1="0" x2="450" y2="40" stroke="url(#lgV)" strokeWidth="1.5"/>
                <line x1="112" y1="40" x2="788" y2="40" stroke="url(#lgH)" strokeWidth="1.5"/>
                <line x1="112" y1="40" x2="112" y2="80" stroke="#4ade80" strokeWidth="1.5" strokeOpacity="0.3"/>
                <line x1="338" y1="40" x2="338" y2="80" stroke="#6366f1" strokeWidth="1.5" strokeOpacity="0.3"/>
                <line x1="563" y1="40" x2="563" y2="80" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.3"/>
                <line x1="788" y1="40" x2="788" y2="80" stroke="#ec4899" strokeWidth="1.5" strokeOpacity="0.3"/>
              </svg>

              {/* AI Team Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
                {([
                  {name:'Aria',role:'Sales Agent',color:'#4ade80',metrics:[{l:'Chats today',v:'284'},{l:'Qualified leads',v:'94%'},{l:'Avg response',v:'1.2s'}],channel:'WhatsApp',
                    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>},
                  {name:'Kira',role:'Support Agent',color:'#6366f1',metrics:[{l:'Tickets this week',v:'1,247'},{l:'Auto-resolved',v:'80%'},{l:'Satisfaction',v:'4.9★'}],channel:'Telegram',
                    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>},
                  {name:'Zara',role:'Finance Agent',color:'#f59e0b',metrics:[{l:'Invoices sent',v:'47'},{l:'Collection rate',v:'+40%'},{l:'Overdue flagged',v:'3'}],channel:'WhatsApp',
                    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>},
                  {name:'Mira',role:'HR Agent',color:'#ec4899',metrics:[{l:'CVs screened',v:'38'},{l:'Interviews booked',v:'12'},{l:'Onboarding active',v:'4'}],channel:'Telegram',
                    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>},
                ] as {name:string;role:string;color:string;metrics:{l:string;v:string}[];channel:string;icon:React.ReactNode}[]).map((ai) => (
                  <div key={ai.name} className="rounded-[18px] p-5 relative transition-all hover:-translate-y-1" style={{background:'#141414',border:'1px solid #232323'}}>
                    <div className="absolute top-0 left-5 right-5 h-px" style={{background:`linear-gradient(90deg,transparent,${ai.color},transparent)`}}></div>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3.5" style={{background:`rgba(${ai.color === '#4ade80' ? '74,222,128' : ai.color === '#6366f1' ? '99,102,241' : ai.color === '#f59e0b' ? '245,158,11' : '236,72,153'},0.08)`,border:`1px solid rgba(${ai.color === '#4ade80' ? '74,222,128' : ai.color === '#6366f1' ? '99,102,241' : ai.color === '#f59e0b' ? '245,158,11' : '236,72,153'},0.15)`}}>
                      {ai.icon}
                    </div>
                    <div className="font-semibold text-[14px] text-white mb-0.5">{ai.name}</div>
                    <div className="text-[10px] font-semibold tracking-[0.1em] uppercase mb-3.5" style={{color:ai.color}}>{ai.role}</div>
                    <div className="flex flex-col gap-1.5">
                      {ai.metrics.map((m) => (
                        <div key={m.l} className="flex items-center justify-between">
                          <span className="text-[11px] text-[#888]">{m.l}</span>
                          <span className="text-[11px] font-semibold text-[#bbb]">{m.v}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 mt-3.5 pt-3.5 text-[11px] text-[#777]" style={{borderTop:'1px solid #1e1e1e'}}>
                      <span className="w-[5px] h-[5px] rounded-full bg-[#22c55e] inline-block"></span>Online · {ai.channel}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pillars inside dark card */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {([
                  {n:'01',t:'You set the strategy',d:'Define goals, boundaries and escalation rules. AI executes exactly as instructed.'},
                  {n:'02',t:'AI handles execution',d:'Every chat, invoice, ticket and interview — handled 24/7 without you lifting a finger.'},
                  {n:'03',t:'Escalation to you',d:'Anything beyond scope is flagged to you instantly. You always have final say.'},
                ] as {n:string;t:string;d:string}[]).map((p) => (
                  <div key={p.n} className="flex gap-4 items-start rounded-2xl p-5" style={{border:'1px solid #1e1e1e'}}>
                    <div style={{...serifStyle,fontSize:36,color:'#3d3d3d',lineHeight:1,flexShrink:0,marginTop:-2}}>{p.n}</div>
                    <div>
                      <div className="text-[13px] font-semibold text-[#aaa] mb-1">{p.t}</div>
                      <div className="text-[12px] text-[#777] leading-relaxed">{p.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Process</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white mb-12">Up and running<br/><em className="italic text-gray-400">in five steps</em></h2>
            <div className="flex flex-col gap-0">
              {steps.map((step: any, i: number) => (
                <div key={i}>
                  <div className="flex gap-5">
                    <div className="text-xs font-semibold text-gray-300 dark:text-gray-600 pt-1 w-7 flex-shrink-0">{step.num || `0${i+1}`}</div>
                    <div className="pb-8">
                      <div className="text-sm font-semibold text-[#1a1a1a] dark:text-white mb-1.5">{step.title}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</div>
                    </div>
                  </div>
                  {i < steps.length - 1 && <div className="ml-7 mb-0 h-px bg-[#E8E8E4] dark:bg-gray-800"></div>}
                </div>
              ))}
              <div className="mt-6 ml-7">
                <Link href="/signup?redirect=/onboarding" className="inline-block bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] px-7 py-3 rounded-full text-sm font-semibold hover:bg-black dark:hover:bg-gray-200 transition">
                  Start Hiring Today
                </Link>
              </div>
            </div>
          </div>
          {/* Dashboard preview */}
          <div className="bg-[#FAFAF8] dark:bg-gray-950 border border-[#E8E8E4] dark:border-gray-800 rounded-2xl p-6">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-5">Your dashboard</div>
            {[
              { name: 'Aria', role: 'Sales Agent', channel: 'WhatsApp', chats: '284', resolved: '94%', response: '1.2s' },
              { name: 'Kira', role: 'Support Agent', channel: 'Telegram', chats: '139', resolved: '81%', response: '0.9s' },
            ].map((agent) => (
              <div key={agent.name} className="bg-white dark:bg-gray-900 border border-[#E8E8E4] dark:border-gray-800 rounded-xl p-4 mb-3 last:mb-0">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-[#1a1a1a] dark:text-white">{agent.name}</div>
                    <div className="text-xs text-gray-400">{agent.role} — {agent.channel}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs text-green-600 font-medium">Active</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: agent.chats, label: 'Chats today' },
                    { val: agent.resolved, label: 'Resolved' },
                    { val: agent.response, label: 'Avg response' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-[#FAFAF8] dark:bg-gray-950 rounded-lg p-2 text-center">
                      <div className="text-sm font-bold text-[#1a1a1a] dark:text-white">{stat.val}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Department in Action ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Use Cases</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Your AI department <em className="italic text-gray-400">in action</em></h2>
          </div>
          {/* Tab bar */}
          <div className="flex gap-0 border-b border-[#E8E8E4] dark:border-gray-800 mb-10 overflow-x-auto">
            {useCases.map((uc, i) => (
              <button key={uc.id} onClick={() => setActiveTab(i)}
                className={`px-0 pb-3 mr-8 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${activeTab === i ? 'text-[#1a1a1a] dark:text-white border-[#1a1a1a] dark:border-white' : 'text-gray-400 border-transparent hover:text-gray-600 dark:hover:text-gray-300'}`}
                style={{ marginBottom: -1 }}>
                {useCases[i].id.charAt(0).toUpperCase() + useCases[i].id.slice(1)}
              </button>
            ))}
          </div>
          {/* Tab content */}
          {useCases.map((uc, i) => i === activeTab && (
            <div key={uc.id} className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
              <div>
                <div className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: uc.roleColor }}>{uc.roleLabel}</div>
                <h3 style={serifStyle} className="text-3xl sm:text-4xl text-[#1a1a1a] dark:text-white mb-4 leading-tight">
                  {uc.title}<br/><em className="italic">{uc.titleItalic}</em>
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">{uc.desc}</p>
                <div className="flex flex-col gap-3 mb-8">
                  {uc.bullets.map((b, j) => (
                    <div key={j} className="flex gap-3 items-start">
                      <div className="w-4 h-4 rounded-full border border-[#1a1a1a] dark:border-white flex-shrink-0 mt-0.5"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-300">{b}</span>
                    </div>
                  ))}
                </div>
                <button className="bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-gray-800 dark:hover:bg-gray-200 transition">
                  Hire {uc.hireName} →
                </button>
              </div>
              <div className="bg-white dark:bg-gray-900 border border-[#E8E8E4] dark:border-gray-800 rounded-2xl p-6">
                <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-4">Live preview</div>
                <div className="flex flex-col gap-3">
                  {uc.preview.map((msg, j) => (
                    <div key={j} className={msg.from === 'user' ? 'flex justify-end' : ''}>
                      <div className={`text-xs leading-relaxed px-3.5 py-2.5 max-w-[85%] whitespace-pre-line ${
                        msg.from === 'user'
                          ? 'bg-[#1a1a1a] text-white rounded-2xl rounded-br-sm'
                          : 'bg-[#F0F0EB] dark:bg-gray-800 text-[#1a1a1a] dark:text-white rounded-2xl rounded-bl-sm'
                      }`}>{msg.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Platform</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Everything your department needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border border-[#E8E8E4] dark:border-gray-800 rounded-2xl overflow-hidden divide-y divide-x divide-[#E8E8E4] dark:divide-gray-800" style={{ background: '#E8E8E4' }}>
            {features.map((f: any, i: number) => (
              <div key={i} className="bg-white dark:bg-gray-900 p-8">
                <div className="text-[11px] font-semibold text-gray-300 dark:text-gray-600 tracking-widest mb-4">{f.num || `0${i+1}`}</div>
                <div className="text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">{f.title}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Pricing</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white mb-6">Simple, transparent pricing</h2>
            {/* Toggle */}
            <div className="inline-flex items-center bg-[#F0F0EB] dark:bg-gray-800 rounded-full p-1 gap-1">
              <button onClick={() => setPricingTab('department')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${pricingTab === 'department' ? 'bg-[#1a1a1a] text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white'}`}>
                AI Department
              </button>
              <button onClick={() => setPricingTab('agent')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${pricingTab === 'agent' ? 'bg-[#1a1a1a] text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white'}`}>
                AI Agent
              </button>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
              {pricingTab === 'department' ? 'Each AI employee billed separately. Start with one, scale to a full team.' : 'Dedicated server with your own AI Super Agent, powered by Claude.'}
            </p>
          </div>

          {/* Department pricing */}
          {pricingTab === 'department' && (
            <div className="flex justify-center">
              {departmentPricing.map((p: any, i: number) => (
                <div key={i} className={`rounded-2xl p-8 border ${p.popular ? 'bg-[#1a1a1a] border-[#1a1a1a] text-white' : 'bg-white dark:bg-gray-900 border-[#E8E8E4] dark:border-gray-800'}`}>
                  <div className="text-[11px] font-semibold tracking-widest uppercase mb-5 text-gray-400">
                    {p.name} {p.popular && <span className="ml-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">Popular</span>}
                  </div>
                  <div className={`text-4xl font-bold mb-1 ${p.popular ? 'text-white' : 'text-[#1a1a1a] dark:text-white'}`}>
                    {p.price}<span className="text-sm font-normal text-gray-400">{p.period}</span>
                  </div>
                  <div className="text-sm mb-6 text-gray-400">{p.desc}</div>
                  <ul className="flex flex-col gap-2.5 mb-7">
                    {(p.features || []).map((f: string, j: number) => (
                      <li key={j} className={`text-sm flex items-center gap-2.5 ${p.popular ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>
                        <div className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${p.popular ? 'bg-gray-700' : 'bg-[#E8E8E4] dark:bg-gray-800'}`}>
                          <svg viewBox="0 0 10 8" fill="none" stroke={p.popular ? '#fff' : '#1a1a1a'} strokeWidth="1.5" className="w-2.5 h-2.5 dark:stroke-white"><polyline points="1,4 4,7 9,1"/></svg>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>
                  {p.note && (
                    <div className={"text-[11px] font-semibold px-3 py-2 rounded-lg mb-4 flex items-center gap-1.5 " + (p.popular ? "bg-gray-700 text-green-400" : "bg-green-50 text-green-700")}>
                      <svg viewBox="0 0 12 12" fill="currentColor" className="w-3 h-3 flex-shrink-0 mr-1"><path d="M10.28 1.28L4.5 7.06 1.72 4.28A1 1 0 00.28 5.72l3.5 3.5a1 1 0 001.44 0l6.5-6.5a1 1 0 00-1.44-1.44z"/></svg>
                      {p.note}
                    </div>
                  )}
                  {p.name === 'Enterprise' ? (
                    <a href="#" className={`block text-center py-2.5 rounded-full text-sm font-semibold transition border ${p.popular ? 'bg-white text-[#1a1a1a] hover:bg-gray-100' : 'border-[#1a1a1a] dark:border-white text-[#1a1a1a] dark:text-white hover:bg-[#1a1a1a] hover:text-white dark:hover:bg-white dark:hover:text-[#1a1a1a]'}`}>Contact sales</a>
                  ) : (
                    <Link href="/signup" className={`block text-center py-2.5 rounded-full text-sm font-semibold transition ${p.popular ? 'bg-white text-[#1a1a1a] hover:bg-gray-100' : 'bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] hover:bg-black dark:hover:bg-gray-200'}`}>
                      {p.cta || 'Get started'}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Agent pricing */}
          {pricingTab === 'agent' && (
            <div className="grid md:grid-cols-2 gap-5 max-w-2xl mx-auto">
              {agentPricing.map((p, i) => (
                <div key={i} className={`rounded-2xl p-8 border ${p.popular ? 'bg-[#1a1a1a] border-[#1a1a1a] text-white' : 'bg-white dark:bg-gray-900 border-[#E8E8E4] dark:border-gray-800'}`}>
                  <div className="text-[11px] font-semibold tracking-widest uppercase mb-5 text-gray-400">
                    {p.name} {p.popular && <span className="ml-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">Popular</span>}
                  </div>
                  <div className={`text-4xl font-bold mb-1 ${p.popular ? 'text-white' : 'text-[#1a1a1a] dark:text-white'}`}>
                    {p.price}<span className="text-sm font-normal text-gray-400">{p.period}</span>
                  </div>
                  <div className="text-sm mb-6 text-gray-400">{p.desc}</div>
                  <ul className="flex flex-col gap-2.5 mb-7">
                    {p.features.map((f, j) => (
                      <li key={j} className={`text-sm flex items-center gap-2.5 ${p.popular ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>
                        <div className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${p.popular ? 'bg-gray-700' : 'bg-[#E8E8E4] dark:bg-gray-800'}`}>
                          <svg viewBox="0 0 10 8" fill="none" stroke={p.popular ? '#fff' : '#1a1a1a'} strokeWidth="1.5" className="w-2.5 h-2.5 dark:stroke-white"><polyline points="1,4 4,7 9,1"/></svg>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>
                  {p.note && (
                    <div className={"text-[11px] font-semibold px-3 py-2 rounded-lg mb-4 flex items-center gap-1.5 " + (p.popular ? "bg-gray-700 text-green-400" : "bg-green-50 text-green-700")}>
                      <svg viewBox="0 0 12 12" fill="currentColor" className="w-3 h-3 flex-shrink-0 mr-1"><path d="M10.28 1.28L4.5 7.06 1.72 4.28A1 1 0 00.28 5.72l3.5 3.5a1 1 0 001.44 0l6.5-6.5a1 1 0 00-1.44-1.44z"/></svg>
                      {p.note}
                    </div>
                  )}
                  <Link href="/login" className={`block text-center py-2.5 rounded-full text-sm font-semibold transition ${p.popular ? 'bg-white text-[#1a1a1a] hover:bg-gray-100' : 'bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] hover:bg-black dark:hover:bg-gray-200'}`}>
                    {p.cta}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">FAQ</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Common questions</h2>
          </div>
          <div className="divide-y divide-[#E8E8E4] dark:divide-gray-800">
            {faq.map((item: any, i: number) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 style={serifStyle} className="text-4xl sm:text-6xl text-white mb-4 leading-tight">
            Build your AI department <em className="italic">today</em>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed">{cta.subtitle}</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link href="/ai-employee-marketplace" className="inline-block bg-white text-[#1a1a1a] px-8 py-3.5 rounded-full text-base font-semibold hover:bg-gray-100 transition">
              Browse AI Employees
            </Link>
            <Link href="/signup" className="inline-block border border-gray-700 text-gray-400 px-8 py-3.5 rounded-full text-base hover:border-gray-500 hover:text-gray-200 transition">
              Start free
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-gray-600 text-sm">
            <span>No credit card</span>
            <span>No engineers needed</span>
            <span>Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[#FAFAF8] dark:bg-gray-950 text-gray-400 py-14 px-3 sm:px-6 border-t border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-4 gap-8 mb-10">
            <div className="sm:col-span-2">
              <img src={logoConfig?.darkUrl || '/logo-dark.png'} alt="Preplix" style={{ height: `${footerPx}px` }} className="mb-4 dark:hidden" />
              <img src={logoConfig?.whiteUrl || '/logo-white.png'} alt="Preplix" style={{ height: `${footerPx}px` }} className="mb-4 hidden dark:block" />
              <p className="text-sm text-gray-400 max-w-xs leading-relaxed">Hire AI employees for your business — Sales, Support, Finance and HR on WhatsApp & Telegram.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-500 dark:text-gray-400 text-sm mb-4">Product</h4>
              <div className="space-y-2 text-sm">
                <Link href="/ai-employee-marketplace" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Marketplace</Link>
                <Link href="/ai-agent" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Single Agent</Link>
                <a href="#pricing" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Pricing</a>
                <Link href="/login" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Get Started</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-500 dark:text-gray-400 text-sm mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <Link href="/about" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">About</Link>
                <Link href="/contact" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Contact</Link>
                <Link href="/terms" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Terms</Link>
                <Link href="/privacy" className="block hover:text-[#1a1a1a] dark:hover:text-white transition">Privacy</Link>
              </div>
            </div>
          </div>
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-4 mb-8">
              {socialLinks.map((s: any, i: number) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition-all duration-200">
                  <span className="text-xs font-bold">{s.platform?.charAt(0)?.toUpperCase()}</span>
                </a>
              ))}
            </div>
          )}
          <div className="border-t border-[#E8E8E4] dark:border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">{footerConfig.copyright || '© 2026 Preplix. All rights reserved.'}</p>
            <div className="flex items-center gap-4 text-sm">
              <Link href="/login" className="inline-flex items-center hover:text-[#1a1a1a] dark:hover:text-white transition">Sign In</Link>
              <Link href="/signup" className="inline-flex items-center hover:text-[#1a1a1a] dark:hover:text-white transition">Sign Up</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="py-5">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left gap-4">
        <span className="text-sm font-semibold text-[#1a1a1a] dark:text-white">{q}</span>
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
      </button>
      {open && <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{a}</p>}
    </div>
  )
}
